import type { NotificationPrefsView, NotifyCategory } from '@hoopoe/shared';
import * as Dialog from '@radix-ui/react-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { FieldHelp } from '../guidance/field-help';
import { Button } from '../ui/button';
import { useReauth } from './reauth';

type Choices = Partial<Record<NotifyCategory, boolean>>;
type Update = { digest?: boolean; email?: Choices; app?: Choices; all?: false; reset?: true };

/**
 * Notification settings (D472): per category, in the app and by email, each on or off; one email a day if wanted;
 * all off, or use the defaults. Opened from the account menu for oneself, or by an admin from Users & access for
 * someone else (`user`; those changes need a fresh authenticator check and are audited).
 */
export function NotificationSettingsDialog({
  open,
  onOpenChange,
  user,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user?: { id: string; email: string };
}) {
  const qc = useQueryClient();
  const { withReauth } = useReauth();
  const [error, setError] = useState<string | null>(null);
  const path = user ? `/admin/users/${user.id}/notifications` : '/me/notifications';
  const key = user ? ['admin', 'notifications', user.id] : ['me', 'notifications'];
  const prefs = useQuery({ queryKey: key, queryFn: () => api<NotificationPrefsView>(path), enabled: open });
  // What the dialog shows: the last answer, or the choice just made (a controlled box must change on the click itself).
  const [local, setLocal] = useState<NotificationPrefsView | null>(null);
  const save = useMutation({
    mutationFn: (body: Update) => {
      const put = () => api<NotificationPrefsView>(path, { method: 'PUT', body });
      return user ? withReauth(put) : put();
    },
    onSuccess: data => {
      setError(null);
      setLocal(data);
      qc.setQueryData(key, data);
      if (user) qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: e => {
      setLocal(null);
      qc.invalidateQueries({ queryKey: key });
      if (e instanceof ApiError && e.code === 'reauth_cancelled') return;
      setError(e instanceof ApiError ? e.message : 'The choice could not be saved. Try again.');
    },
  });
  const p = local ?? prefs.data;
  const change = (body: Update) => {
    if (p && !body.all && !body.reset)
      setLocal({
        ...p,
        digest: body.digest ?? p.digest,
        categories: p.categories.map(c => ({
          ...c,
          inApp: body.app?.[c.key] ?? c.inApp,
          email: c.locked ? c.email : (body.email?.[c.key] ?? c.email),
        })),
      });
    save.mutate(body);
  };
  const whose = user ? `${user.email}’s` : 'your';
  return (
    <Dialog.Root
      open={open}
      onOpenChange={o => {
        if (!o) {
          setLocal(null);
          setError(null);
        }
        onOpenChange(o);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed top-1/2 left-1/2 z-50 max-h-[90vh] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 space-y-4 overflow-y-auto rounded-lg border bg-surface-raised p-5 shadow-lg">
          <div className="flex items-center justify-between gap-2">
            <Dialog.Title className="flex min-w-0 items-center gap-1 font-semibold">
              <span className="truncate">{user ? `Notifications for ${user.email}` : 'Notification settings'}</span>
              <FieldHelp
                label="Notification settings"
                help="Each kind of notification can show in the app, under the bell, and come by email. Turn each one on or off, or turn them all off."
                learnMore="email-notifications"
              />
            </Dialog.Title>
            <Dialog.Close aria-label="Close" className="rounded p-0.5 text-muted-foreground hover:text-foreground">
              <X className="size-4" aria-hidden="true" />
            </Dialog.Close>
          </div>
          <Dialog.Description className="text-sm text-muted-foreground">
            Choose which notifications {user ? 'this person gets' : 'you get'}, in the app and by email.
          </Dialog.Description>
          {error && (
            <p role="alert" className="text-sm text-danger-foreground">
              {error}
            </p>
          )}
          {!p ? (
            <p className="text-sm text-muted-foreground">{prefs.isError ? 'The choices could not be loaded.' : 'Loading…'}</p>
          ) : (
            <>
              {p.allOff && (
                <p className="rounded-md border bg-muted px-3 py-2 text-sm" data-testid="notifications-all-off">
                  All notifications are off{p.categories.some(c => c.locked) ? ', except system alert emails' : ''}.
                </p>
              )}
              <ul className="divide-y rounded-lg border bg-surface" aria-label="Notification categories">
                {p.categories.map(cat => (
                  <li key={cat.key} className="flex items-start gap-3 p-3 text-sm">
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <p className="font-medium">{cat.label}</p>
                      <p className="text-xs text-muted-foreground">{cat.help}</p>
                      {cat.locked && <p className="text-xs text-muted-foreground">{cat.locked}</p>}
                    </div>
                    <div className="flex shrink-0 flex-col items-start gap-3 text-xs">
                      <label className="flex items-center gap-1.5">
                        <input type="checkbox" checked={cat.inApp} onChange={e => change({ app: { [cat.key]: e.target.checked } })} />
                        In the app <span className="sr-only">for {cat.label}</span>
                      </label>
                      <label className="flex items-center gap-1.5">
                        <input
                          type="checkbox"
                          checked={cat.email}
                          disabled={!!cat.locked}
                          onChange={e => change({ email: { [cat.key]: e.target.checked } })}
                        />
                        Email <span className="sr-only">for {cat.label}</span>
                      </label>
                    </div>
                  </li>
                ))}
              </ul>
              <fieldset className="space-y-1.5 text-sm">
                <legend className="mb-1 font-medium">How often emails come</legend>
                <label className="flex items-center gap-2">
                  <input type="radio" name="email-often" checked={!p.digest} onChange={() => change({ digest: false })} />
                  As they happen
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" name="email-often" checked={p.digest} onChange={() => change({ digest: true })} />
                  One email a day (a daily summary)
                </label>
              </fieldset>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={p.allOff || save.isPending}
                  onClick={() => {
                    if (confirm(`Turn off all of ${whose} notifications, in the app and by email?`)) change({ all: false });
                  }}
                >
                  Turn off all notifications
                </Button>
                <Button size="sm" variant="ghost" disabled={p.defaults || save.isPending} onClick={() => change({ reset: true })}>
                  Use the defaults
                </Button>
              </div>
              <p className="text-xs text-muted-foreground" aria-live="polite">
                {save.isPending ? 'Saving…' : save.isSuccess ? 'Saved.' : 'Changes are saved as you make them.'}
              </p>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
